// Traffic guide for "fantasy football punishments" (OpenSEO US 2026-11-02:
// 8,100/mo, KD 0) and "fantasy football last place trophy" (390/mo, KD 0).
// The ideas are a list, stored here so the test can count them; every one is
// PG-13, legal, no alcohol and nothing permanent (see `safetyRules`).
// Trophy claims stay inside the custom-trophy boundaries in .seo/truth.md:
// prices come from ./custom-trophy, no fixed turnaround, plating is not solid metal.
const { pricing } = require('./custom-trophy')

const [paintedTier, platedTier] = pricing.tiers
const startingPrice = `$${pricing.from}`
const modelingFee = `$${pricing.modelingFee}`

// [id, heading, intro, [[punishment, why it works], ...]]
const categories = [
  [
    'food-challenges',
    'Food challenges',
    'The classics. Keep the portions sane and let the loser tap out; quitting on camera is half the fun.',
    [
      [
        'The 24-hour diner',
        'Sit in a 24-hour diner for a day. Every waffle eaten takes an hour off the clock. The loser picks the pace, the league gets updates all day.',
      ],
      [
        'The hot-sauce ladder',
        'Wings up a ladder of hotter sauces, filmed interview-style. Tapping out early is allowed and gets replayed for years.',
      ],
      [
        'The league’s worst pizza',
        'Every manager picks one topping. Pineapple, anchovies, pickles, sardines. The loser eats a slice of whatever shows up.',
      ],
      [
        'Blind baby-food tasting',
        'Five unlabeled jars, guess every flavor on video. Gross, harmless and cheap.',
      ],
      [
        'Cook for the champion',
        'A three-course dinner for the winner, served in an apron the league picks. The champ gets fed, the loser gets humbled.',
      ],
      [
        'Kids’ menu at the league dinner',
        'The loser orders only from the kids’ menu at the season-end dinner, crayons included.',
      ],
      [
        'The mystery smoothie',
        'Each manager adds one food-safe ingredient to the blender. The loser drinks one cup. Check allergies first.',
      ],
      [
        'Snack duty all next season',
        'The loser brings the food to every draft and watch party. Useful to everyone, mildly painful for one.',
      ],
    ],
  ],
  [
    'public-embarrassment',
    'Public embarrassment',
    'The ones people tell stories about. Embarrassing, not mean, and never at a stranger’s expense.',
    [
      [
        'Fake birthday at a chain restaurant',
        'Tell the server it’s your birthday. It isn’t. Sit through the song and the sombrero.',
      ],
      [
        'Five minutes at an open mic',
        'Stand-up about your own season. Bombing is fine. Bombing is the point.',
      ],
      [
        'The sign of shame',
        'Hold a “I finished last in fantasy football” sign on a busy sidewalk for an hour. Stay on public property and out of traffic.',
      ],
      [
        'League-picked karaoke',
        'The league picks the song. The loser sings all of it, no skipping the bridge.',
      ],
      [
        'The apology letter',
        'Write a heartfelt letter of apology to the league for wasting everyone’s time, then read it out loud at the next get-together.',
      ],
      [
        'Mascot costume errands',
        'Wear a full mascot or animal costume to do the weekly grocery run. Strangers smile; nobody gets hurt.',
      ],
      [
        'The adult lemonade stand',
        'Run a lemonade stand for three hours with a sign explaining why. Check local rules and give the money to charity.',
      ],
      [
        'Rival jersey at a watch party',
        'Show up to a bar or watch party in your rival team’s jersey and cheer for them, loudly, all game.',
      ],
      [
        'The one-person flash mob',
        'One minute of dancing in a busy park to a song the league picks. Filmed from a distance for maximum sadness.',
      ],
    ],
  ],
  [
    'wearables-and-photos',
    'Wearables and photos',
    'Low effort for the league, long shelf life. Photos last longer than any food challenge.',
    [
      [
        'The champion picks your jersey',
        'Wear a jersey of the winner’s choice for a week, everywhere, including work if allowed.',
      ],
      [
        'The league T-shirt',
        'A custom shirt printed with your worst lineup decision. Worn every Sunday until the next draft.',
      ],
      [
        'Glamour photo shoot',
        'A cheesy portrait-studio shoot, laser background and all. The best print gets framed for the champion.',
      ],
      [
        'The league haircut',
        'The league picks the cut: mullet, bowl cut, frosted tips. It grows back. Get the loser’s yes before the season starts.',
      ],
      [
        'A temporary tattoo',
        'A league-designed temporary tattoo, refreshed weekly for a month. Temporary only, never real ink.',
      ],
      [
        'Fanny pack and visor season',
        'The loser wears a fanny pack and a visor to every league event next year.',
      ],
      [
        'League-picked profile picture',
        'The league picks the loser’s profile photo for a month. Choose wisely; the loser picks next year.',
      ],
      [
        'Sash and tiara at the draft',
        'A pageant sash that says “Last Place” and a plastic tiara, worn the whole draft night.',
      ],
      [
        'The bumper sticker',
        'A league bumper sticker on the loser’s car for the season. Every red light becomes a reminder.',
      ],
    ],
  ],
  [
    'charity-and-donations',
    'Charity and donations',
    'For leagues that want the loss to do some good. These also make good swaps when a punishment doesn’t fit someone.',
    [
      [
        'The champion’s charity',
        'The loser donates a set amount to a charity the winner picks. Agree on the amount before the draft.',
      ],
      [
        'Donate to the rival’s foundation',
        'A donation to your rival team’s charity foundation. Good cause, real pain for a fan.',
      ],
      [
        'A volunteer day in league gear',
        'A day at a food bank or animal shelter, wearing the league shirt. Everybody wins except the loser’s pride.',
      ],
      [
        'Charity 5K in costume',
        'Run or walk a charity 5K in a costume the league picks. Walking counts.',
      ],
      [
        'A dime per point',
        'Donate ten cents for every point you finished behind the champion. Bad seasons get expensive in a fair way.',
      ],
      [
        'Run the office coat drive',
        'The loser organizes a coat or toy drive and wears a “brought to you by last place” badge doing it.',
      ],
      [
        'Double dues to charity',
        'Pay next year’s league dues twice. The extra goes to a charity the league votes on.',
      ],
    ],
  ],
  [
    'draft-day',
    'Draft-day punishments',
    'Paid at the next draft, when the whole league is in one room or one call.',
    [
      [
        'Last pick in the first round',
        'The loser drafts last next year. A real consequence that costs nothing and embarrasses no one.',
      ],
      [
        'Draft in costume',
        'The league picks the costume. The loser wears it from first pick to last.',
      ],
      [
        'Draft-board duty',
        'The loser buys the draft board, sets it up and places every sticker for every manager.',
      ],
      [
        'Host and clean up',
        'The loser hosts the draft and does all the dishes after. Practical, and everybody likes it.',
      ],
      [
        'The kids’ table',
        'The loser drafts from a tiny chair at a folding kids’ table, away from the adults.',
      ],
      [
        'Read every pick aloud',
        'The loser announces every pick, podium style, with a fake mustache. That’s a lot of picks.',
      ],
      [
        'The league picks your first-rounder',
        'The league picks the loser’s first-round pick from a shortlist. Keep it to real players; no kickers in round one, please.',
      ],
      [
        'The draft-night roast',
        'Before each round, the loser gets roasted by one manager for 30 seconds. Keep it PG-13 and about football.',
      ],
    ],
  ],
  [
    'season-long',
    'Season-long punishments',
    'Small daily doses all year. These outlast any single bad night and make the loser want to win.',
    [
      [
        'Keep the last-place trophy',
        'The shame trophy lives in the loser’s home for a year and comes to every league event. More on it below.',
      ],
      [
        'The league names your team',
        'The league chooses the loser’s team name for the whole next season. They have to answer to it.',
      ],
      [
        'Weekly newsletter writer',
        'The loser writes the weekly league recap all next season, with power rankings and jokes.',
      ],
      [
        'League-picked ringtone',
        'The league picks the loser’s ringtone for the season. Something loud. Something terrible.',
      ],
      [
        'Third person in the group chat',
        'The loser can only refer to themselves in the third person in the league chat for a month.',
      ],
      [
        'Weekly trophy selfie',
        'A selfie with the last-place trophy, posted every Monday all season.',
      ],
      [
        'Fan of the champion’s team',
        'The loser has to cheer for the champion’s favorite team all next season and post about it.',
      ],
      [
        'Pay for your own nameplate',
        'The loser pays to have their own name added to the shame trophy. Forever.',
      ],
    ],
  ],
  [
    'office-league',
    'Office-league safe',
    'HR-friendly ideas for work leagues. Nothing that could embarrass someone in front of a client or a boss.',
    [
      [
        'Desk shrine to the champion',
        'The loser keeps a framed photo of the champion on their desk for a month, with a little candle (battery only).',
      ],
      [
        'Breakfast for the office',
        'Bagels or donuts for the whole floor, with a card explaining why.',
      ],
      [
        'The trophy lives on your desk',
        'The last-place trophy sits on the loser’s desk until the next season ends. Coworkers will ask.',
      ],
      [
        'The email signature',
        'Add “Last place, 2026 office league” to internal email signatures for a month. Internal only.',
      ],
      [
        'The champion decorates your desk',
        'The champ gets one hour and a small budget to decorate the loser’s desk in their team’s colors.',
      ],
      [
        'Coffee runs for the champion',
        'The loser brings the winner their coffee order every morning for a week.',
      ],
      [
        'A haiku for every manager',
        'The loser writes a haiku about each manager’s season and reads them at a team lunch.',
      ],
      [
        'Jersey Friday',
        'The loser wears the champion’s pick of jersey to the office every Friday for a month.',
      ],
    ],
  ],
  [
    'remote-league',
    'Remote-league ideas',
    'For leagues spread across states. All of these work over video, mail or the group chat.',
    [
      [
        'Costume on the draft call',
        'The loser joins the next draft video call in a costume the league picks, camera on all night.',
      ],
      [
        'League-picked video background',
        'The league picks the loser’s video-call background for a month. Work calls included, if they’re brave.',
      ],
      [
        'Handwritten apology postcards',
        'A handwritten apology postcard mailed to every manager. Real stamps, real shame.',
      ],
      [
        'The livestreamed food challenge',
        'Any food challenge above, streamed live to the league. Viewers vote on the next bite.',
      ],
      [
        'The sad ballad',
        'Write and record a breakup song about your season. Post it in the group chat.',
      ],
      [
        'The hometown snack box',
        'The loser mails the champion a box of snacks from their city. Postage paid by the loser.',
      ],
      [
        'The news anchor recap',
        'Read the season recap on camera like a nightly news anchor, tie and serious face required.',
      ],
      [
        'In memoriam video',
        'Make a slow-motion tribute video to your dead season, sad piano and all.',
      ],
      [
        'Draft-night delivery for everyone',
        'The loser sends a food delivery gift card to every manager on draft night.',
      ],
    ],
  ],
]

const ideas = categories.flatMap(([, , , list]) => list)
const ideaCount = ideas.length

const fantasyFootballPunishments = {
  path: '/guides/fantasy-football-punishments/',
  title: `Fantasy Football Punishments: ${ideaCount} Ideas for Last Place`,
  cardTitle: `${ideaCount} fantasy football punishments for last place`,
  publishedDate: '2026-11-02',
  reviewedDate: '2026-11-02',
  reviewedDateLabel: 'November 2, 2026',
}

// AI-generated placeholder (owner growth policy rule 5), captioned on the page.
const image = {
  src: (width) =>
    `/images/fantasy-football-punishments/last-place-toilet-trophy-${width}.webp`,
  ratio: 1.5,
  alt: 'A custom last-place fantasy football trophy: a toilet with a cracked football in the bowl and a sad football character slumped beside it, on a wood base with blank nameplates',
  caption: 'AI-generated reference image',
}

const metaTitle = `${ideaCount} Fantasy Football Punishments for Last Place (2026)`
const description = `${ideaCount} fantasy football punishments for last place: food challenges, public shame, draft-day and office-safe ideas, plus the last-place trophy.`
const answer = `The best fantasy football punishments are embarrassing, safe and agreed on before the draft. Below are ${ideaCount} of them in eight groups, from the 24-hour diner and a fake birthday to office-safe and remote ideas. The one that lasts longest is a last-place trophy the loser keeps all year.`

const safetyRules = [
  [
    'No alcohol',
    'Don’t make drinking part of any punishment, and don’t pressure anyone to drink. Every idea here works sober.',
  ],
  [
    'Nothing dangerous',
    'No eating contests to the point of sickness, no extreme peppers, no stunts, no driving dares. Anyone can tap out.',
  ],
  [
    'Nothing illegal',
    'Stay on public property, follow local rules for signs and stands, and leave strangers out of it.',
  ],
  [
    'Nothing permanent',
    'Temporary tattoos only. Hair grows back; skin, cars and careers don’t.',
  ],
  [
    'Keep it PG-13',
    'If the loser’s kids, partner or boss would be upset to see it, pick something else.',
  ],
  [
    'Set a cap and an opt-out',
    'Agree a dollar cap before the draft. Allergies, health or a job reason means a swap, usually a donation.',
  ],
]

const pickingTips = [
  'Vote on the punishment before the draft and write it into the league rules. Nobody can say they didn’t sign up for it.',
  'Give the loser a short list to pick from, chosen by the league. They choose how, not whether.',
  'Set a deadline, like 30 days after the championship, so it actually happens.',
  'Film it. A punishment nobody sees didn’t happen.',
  'Pair one big moment (a food challenge, an open mic) with one long one (the trophy, the team name).',
]

const trophyDesigns = [
  [
    'The toilet bowl',
    'The original. A golden toilet on a base, sometimes with a football sitting in it.',
  ],
  [
    'The Sacko',
    'Made famous by the TV show The League: a sad little sack that goes to last place.',
  ],
  [
    'The deflated football',
    'A flat, sad football on a pedestal. Simple, funny, easy to read from across a room.',
  ],
  [
    'The loser’s face',
    'The last-place manager’s face, crying, as the topper. We sculpt it from a photo.',
  ],
  [
    'The league’s inside joke',
    'The bad trade, the benched star who scored 40, the team that never set a lineup. Turn it into the trophy.',
  ],
  [
    'The participation trophy, but huge',
    'An oversized, over-the-top participation award. The bigger it is, the worse it feels to win.',
  ],
]

const trophyFacts = [
  [
    'Price',
    `From ${startingPrice} each. Painted resin (${paintedTier.size.replace(/^Standard size, /, '')}) runs ${paintedTier.price}; plated, gold or large trophies run ${platedTier.price}.`,
  ],
  [
    'Design',
    `A one-time 3D modeling fee, ${modelingFee} for most projects. The concept mockup is free.`,
  ],
  [
    'Nameplates',
    'A base with room for every season’s loser, so the trophy passes down and the list keeps growing.',
  ],
  [
    'Minimum',
    'None. One last-place trophy is a normal order, and we can quote it with the champion’s trophy.',
  ],
  [
    'Timing',
    'Plan a month or more for the mockup, your approval of the 3D model and finishing. Closer date? Ask.',
  ],
]

const faqs = [
  {
    question: 'What are good fantasy football punishments?',
    answer:
      'Good ones are embarrassing, safe and short: a fake birthday at a chain restaurant, five minutes at an open mic, a 24-hour diner shift, a league-picked team name for a season, or keeping a last-place trophy all year. Agree on it before the draft so nobody can back out.',
  },
  {
    question: 'What is a fantasy football last-place trophy?',
    answer: `It’s a shame trophy that goes to the manager who finished last. The loser keeps it for a year, adds their name to the base and hands it to next season’s loser. Toilets, deflated footballs and the Sacko are popular designs. A custom one from PrintAWorld starts at ${startingPrice}.`,
  },
  {
    question: 'What is the Sacko?',
    answer:
      'The Sacko is the last-place trophy from the TV show The League. Many real leagues copied it, and “the Sacko” is now a common name for any fantasy loser trophy.',
  },
  {
    question: 'How much does a custom last-place trophy cost?',
    answer: `At PrintAWorld, custom trophies start at ${startingPrice}. Painted resin, 8–12 inches tall, runs ${paintedTier.price}; plated, gold or large trophies run ${platedTier.price}, and 16 inches or taller starts at $1,500. Each new design has a one-time modeling fee, ${modelingFee} for most projects. Plating is a metal surface over resin, not solid metal.`,
  },
  {
    question: 'What is a good office fantasy football punishment?',
    answer:
      'Keep it HR-friendly: breakfast for the office, coffee runs for the champion, the last-place trophy on the loser’s desk, or the champ’s jersey every Friday for a month. Skip anything a client or boss would find awkward.',
  },
  {
    question: 'How do you make a fantasy punishment stick?',
    answer:
      'Vote on it before the draft, write it into the league rules, set a deadline after the championship and film it. Leagues that skip the vote usually end up with a punishment nobody does.',
  },
  {
    question: 'Are drinking punishments a good idea?',
    answer:
      'No. Leave alcohol out of it. Plenty of punishments are funnier sober, and nobody should have to drink to stay in a league.',
  },
]

module.exports = {
  fantasyFootballPunishments,
  image,
  metaTitle,
  description,
  answer,
  categories,
  ideas,
  ideaCount,
  safetyRules,
  pickingTips,
  trophyDesigns,
  trophyFacts,
  faqs,
}
